import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { UsuarioService } from '../service/usuario/usuario.service';
import { UtilitarioService } from '../service/utilitarios/utilitario.service';
import { HeaderCommunicationService } from '../service/header/header-communication.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  // Mapeo de rutas a nombres descriptivos
  private readonly routeNames: { [key: string]: string } = {
    '/carrito': 'Carrito de Compras',
    '/procesarpago': 'Procesar Pago',
    '/mi-cuenta': 'Mi Cuenta',
    '/mi-cuenta/mis-pedidos': 'Mis Pedidos'
  };

  // Control para evitar mensajes duplicados
  private lastBlockedUrl: string | null = null;
  private lastBlockedTime: number = 0;

  constructor(
    private usuarioService: UsuarioService,
    private router: Router,
    private utilitarioService: UtilitarioService,
    private headerCommunicationService: HeaderCommunicationService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // Verificar si el usuario tiene sesión activa
    if (this.usuarioService.obtenerSesionUsuario()) {
      return true;
    }

    // Control de duplicados: evitar mostrar el mismo mensaje para la misma URL en menos de 500ms
    const currentTime = Date.now();
    const isDuplicate = this.lastBlockedUrl === state.url && (currentTime - this.lastBlockedTime) < 500;

    if (!isDuplicate) {
      // Obtener el nombre descriptivo de la página
      const pageName = this.getPageName(state.url);

      // Si no está autenticado, mostrar mensaje detallado
      this.utilitarioService.toastrWarning(
        `Para acceder a "${pageName}" necesita iniciar sesión`
      );

      // Guardar la URL intentada para redirigir después del login
      this.usuarioService.setReturnUrl(state.url);

      // Actualizar control de duplicados
      this.lastBlockedUrl = state.url;
      this.lastBlockedTime = currentTime;

      // Redirigir al home
      this.router.navigate(['/']);

      // Abrir el modal de login automáticamente
      // Usamos setTimeout para asegurar que el componente header esté listo
      setTimeout(() => {
        this.headerCommunicationService.requestOpenLoginModal();
      }, 300);
    }

    return false;
  }

  /**
   * Obtiene el nombre descriptivo de la página basado en la URL
   */
  private getPageName(url: string): string {
    // Remover parámetros de query si existen
    const baseUrl = url.split('?')[0];

    // Buscar coincidencia exacta
    if (this.routeNames[baseUrl]) {
      return this.routeNames[baseUrl];
    }

    // Buscar coincidencia parcial (para rutas con parámetros)
    for (const route in this.routeNames) {
      if (baseUrl.startsWith(route)) {
        return this.routeNames[route];
      }
    }

    // Si no se encuentra, devolver la URL limpia
    return baseUrl.replace('/', '').replace(/-/g, ' ').trim() || 'esta página';
  }

}
